import {gql} from '@apollo/client';
import {useMemo} from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {DnaPairEntitiesQuery} from 'antibodies/nested-data-creation/full-screen/__generated/DnaPairEntitiesQuery';
import {
  ProteinCreationCellValueType,
  ProteinCreationRow,
} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {generateSpreadsheetComponentColumns} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {NdcSpreadsheetComponentFormatType} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import sortedBy from 'util/sortedBy';
import {assertNever} from 'util/Types';

export const DNA_PAIR_ENTITIES_QUERY = gql`
  query DnaPairEntitiesQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: MOLBIO) {
    bioentities(ids: $ids) {
      ... on RegistryItemInterface {
        id
        tagSchema {
          ... on TagSchemaNode {
            id
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface UseDnaPairEntityIdsArgs {
  rows: ReadonlyArray<ProteinCreationRow>;
  format: AntibodyFormat;
  startingType: NdcSpreadsheetComponentFormatType;
  dnaPairSchemaIds: ReadonlySet<string> | null;
  shouldFetch: boolean;
}

export interface DnaPairEntityIdsResult {
  dnaPairEntityIds: ReadonlySet<string>;
  isLoading: boolean;
  hasError: boolean;
}

function getPairEntityIds(
  rows: ReadonlyArray<ProteinCreationRow>,
  format: AntibodyFormat,
  startingType: NdcSpreadsheetComponentFormatType
): Array<string> {
  if (startingType !== NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS) {
    return [];
  }
  const pairColumnLabels = generateSpreadsheetComponentColumns(format, startingType)
    .filter(({componentType}) => componentType === NdcSpreadsheetComponentFormatType.VARIABLE_PAIRS)
    .map(({label}) => label);
  const ids = new Set<string>();
  for (const row of rows) {
    for (const label of pairColumnLabels) {
      const value = row.nestedComponents[label];
      if (value?.type === ProteinCreationCellValueType.EXISTING_ENTITY) {
        ids.add(value.entityId);
      }
    }
  }
  return sortedBy([...ids], [(id) => id]);
}

/**
 * Determines which existing pairs in the table's pair columns are DNA pairs, based on whether their schema is one
 * of `dnaPairSchemaIds`. If the lookup fails, or the DNA pair schemas are unknown, every pair is interpreted as DNA.
 */
export default function useDnaPairEntityIds({
  rows,
  format,
  startingType,
  dnaPairSchemaIds,
  shouldFetch,
}: UseDnaPairEntityIdsArgs): DnaPairEntityIdsResult {
  const pairEntityIds = useMemo(
    () => (shouldFetch ? getPairEntityIds(rows, format, startingType) : []),
    [shouldFetch, rows, format, startingType]
  );

  const result = useSkippableBenchlingQuery<DnaPairEntitiesQuery>(DNA_PAIR_ENTITIES_QUERY, {
    variables: {ids: pairEntityIds},
    skip: pairEntityIds.length === 0,
  });

  const resultType = result.type;
  const data = result.type === QueryResultType.SUCCESS ? result.data : null;

  return useMemo(() => {
    switch (resultType) {
      case QueryResultType.SKIPPED:
        return {dnaPairEntityIds: new Set(), isLoading: false, hasError: false};
      case QueryResultType.PENDING:
        return {dnaPairEntityIds: new Set(), isLoading: true, hasError: false};
      case QueryResultType.ERROR:
        return {dnaPairEntityIds: new Set(pairEntityIds), isLoading: false, hasError: true};
      case QueryResultType.SUCCESS: {
        if (data == null || dnaPairSchemaIds == null) {
          return {dnaPairEntityIds: new Set(pairEntityIds), isLoading: false, hasError: false};
        }
        const dnaPairEntityIds = new Set(
          accessibleObjs(data.bioentities)
            .filter(({tagSchema}) => tagSchema != null && dnaPairSchemaIds.has(tagSchema.id))
            .map(({id}) => id)
        );
        return {dnaPairEntityIds, isLoading: false, hasError: false};
      }
      default:
        assertNever(resultType);
        return {dnaPairEntityIds: new Set(pairEntityIds), isLoading: false, hasError: true};
    }
  }, [resultType, data, pairEntityIds, dnaPairSchemaIds]);
}
